import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import {
  PREDEFINED_PLAN_FEATURES,
  OTHER_FEATURE_VALUE,
} from '../../constants/planFeatures';
import './PlanDetail.css';


const PlanDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState([]);
  const [showEditFeaturesModal, setShowEditFeaturesModal] = useState(false);
  const [selectedFeatureOption, setSelectedFeatureOption] = useState('');
  const [customFeature, setCustomFeature] = useState('');
  const [featureError, setFeatureError] = useState('');

  const fetchPlan = async () => {
    try {
      const res = await api.get(`/plans/${id}`)
      
      if (res.data?.success) {
        setSubscription(res.data.data);
        setFeatures(res.data.data.features || []);
      }
    } catch (err) {
      console.error("Failed to fetch plan:", err);
      alert("Failed to fetch plan details");
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [id]);

  const availableFeatureOptions = PREDEFINED_PLAN_FEATURES.filter(
    (feature) =>
      !features.some((f) => f.trim().toLowerCase() === feature.toLowerCase())
  );

  const resetFeaturePicker = () => {
    setSelectedFeatureOption('');
    setCustomFeature('');
    setFeatureError('');
  };

  const handleAddFeature = () => {
    let feature = '';

    if (selectedFeatureOption === OTHER_FEATURE_VALUE) {
      feature = customFeature.trim();
      if (!feature) {
        setFeatureError('Enter a custom feature');
        return;
      }
    } else if (selectedFeatureOption) {
      feature = selectedFeatureOption;
    } else {
      setFeatureError('Select a feature from the list');
      return;
    }

    if (feature.length < 2) {
      setFeatureError('Feature must be at least 2 characters');
      return;
    }

    if (feature.length > 80) {
      setFeatureError('Feature cannot exceed 80 characters');
      return;
    }

    const isDuplicate = features.some(
      (f) => f.trim().toLowerCase() === feature.toLowerCase()
    );
    if (isDuplicate) {
      setFeatureError('This feature is already added');
      return;
    }

    setFeatures([...features, feature]);
    resetFeaturePicker();
  };

  const handleRemoveFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleSaveFeatures = async () => {
    try {
      await api.put(`/admin/plans/${id}`, {
        features: features
      });
      toast.success('Features updated successfully');
      setShowEditFeaturesModal(false);
      resetFeaturePicker();
      fetchPlan();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to update features");
    }
  };

  const planData = subscription
    ? {
        name: subscription.name + ' Plan',
        subscriptionId: subscription.planId,
        validity: subscription.duration,
        unit: subscription.unit || '',
        price: subscription.price.toLocaleString(),
        status: subscription.status,
         description: subscription.description || 'N/A',
        features: features
      }
    : {
        name: 'Loading...',
        subscriptionId: 'N/A',
        validity: 'N/A',
        unit: '',
        price: 'N/A',
        status: 'N/A',
        description: 'N/A',
        features: features
      };

  return (
    <div className="plan-detail">
      <div className="plan-detail-content">
        <div className="detail-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>

        <div className="plan-info-card">
          <h1 className="plan-title">{planData.name}</h1>

          <div className="plan-details">
            <div className="detail-item">
              <span className="label">Subscription Id</span>
              <span className="value">{planData.subscriptionId}</span>
            </div>
            <div className="detail-item">
              <span className="label">Validity</span>
              <span className="value">{planData.validity} {planData.unit}</span>
            </div>
            <div className="detail-item">
              <span className="label">Price (₹)</span>
              <span className="value">{planData.price}</span>
            </div>
            <div className="detail-item">
              <span className="label">Status</span>
<span className={`value status ${planData?.status?.toLowerCase() || ''}`}>
  {planData?.status
    ? planData.status[0].toUpperCase() + planData.status.slice(1)
    : 'N/A'}
</span>
            </div>
              <div className="detail-item">
    <span className="label">Description</span>
    <span className="value">{planData.description}</span>
  </div>

          </div>

          <div className="features-section">
            <div className="features-header">
              <h3>Features</h3>
              <button className="edit-features-btn" onClick={() => {
                resetFeaturePicker();
                setShowEditFeaturesModal(true);
              }}>
                Edit Features
              </button>
            </div>
           <div className="features-list">
  {planData.features && planData.features.length > 0 ? (
    planData.features.map((feature, index) => (
      <div key={index} className="feature-item">
        <span className="feature-number">(0{index + 1})</span>
        <span className="feature-text">{feature}</span>
      </div>
    ))
  ) : (
    <p className="no-data">No feature added yet</p>
  )}
</div>
          </div>
        </div>
      </div>

      {showEditFeaturesModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Features</h3>
            <div className="form-group">
              <label>Features</label>
              <div className="feature-input-container">
                <select
                  className="feature-select"
                  value={selectedFeatureOption}
                  onChange={(e) => {
                    setSelectedFeatureOption(e.target.value);
                    setFeatureError('');
                  }}
                >
                  <option value="">Select a feature</option>
                  {availableFeatureOptions.map((feature) => (
                    <option key={feature} value={feature}>
                      {feature}
                    </option>
                  ))}
                  <option value={OTHER_FEATURE_VALUE}>Other (custom)</option>
                </select>

                {selectedFeatureOption === OTHER_FEATURE_VALUE && (
                  <input
                    type="text"
                    className="custom-feature-input"
                    value={customFeature}
                    onChange={(e) => setCustomFeature(e.target.value.slice(0, 80))}
                    placeholder="Enter custom feature"
                  />
                )}

                <button type="button" className="add-feature-btn" onClick={handleAddFeature}>
                  Add
                </button>
              </div>

              {featureError && (
                <span className="form-error">{featureError}</span>
              )}

              <div className="features-list">
                {features.map((feature, index) => (
                  <div key={`${feature}-${index}`} className="feature-item">
                    <span className="feature-number">(0{index + 1})</span>
                    <span className="feature-text">{feature}</span>
                    <button type="button" className="remove-feature" onClick={() => handleRemoveFeature(index)}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => {
                setShowEditFeaturesModal(false);
                resetFeaturePicker();
                fetchPlan();
              }}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSaveFeatures}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PlanDetail;
